/**
 * Password policy validation (runtime / generated-app side).
 *
 * The policy is configured per-app in the builder (App Settings → "Password
 * policy" tab) and stored in `app.metadata.passwordPolicy`. It is enforced on
 * every end-user password entry point: form password fields, the reset-password
 * screen, and the standalone password element.
 *
 * The error message shown to the end-user is built from the rules the value
 * failed, using the fixed per-rule messages in `getRuleMessage` (no custom
 * message).
 *
 * NOTE: the builder tester duplicates the rule-checking logic in
 * `packages/client/src/components/HeaderProduct/AppSetting/PasswordPolicy/validate.ts`
 * (the two packages do not share code). Keep the rule set and the special-char
 * set below in sync between the two files.
 */

export type PasswordPolicy = {
  enabled?: boolean;
  minLength?: number;
  requireNumber?: boolean;
  requireLowercase?: boolean;
  requireUppercase?: boolean;
  requireSpecial?: boolean;
};

export type PasswordPolicyRule =
  | 'minLength'
  | 'requireNumber'
  | 'requireLowercase'
  | 'requireUppercase'
  | 'requireSpecial';

/** Special characters accepted for `requireSpecial` (matches the login auth set). */
export const PASSWORD_SPECIAL_CHARS = '@$!%*#?&';

const NUMBER_REGEX = /[0-9]/;
const LOWERCASE_REGEX = /[a-z]/;
const UPPERCASE_REGEX = /[A-Z]/;
const SPECIAL_REGEX = /[@$!%*#?&]/;

/** True when the policy exists and is switched on. */
export const isPasswordPolicyActive = (
  policy?: PasswordPolicy | null
): boolean => !!policy && policy.enabled === true;

/**
 * Returns the list of rules the value fails. Empty means the value satisfies
 * the policy (also empty when the policy is inactive).
 */
export const getFailedPasswordRules = (
  value: string | null | undefined,
  policy?: PasswordPolicy | null
): PasswordPolicyRule[] => {
  const failed: PasswordPolicyRule[] = [];
  if (!isPasswordPolicyActive(policy)) return failed;

  const val = value ?? '';
  if (policy!.minLength && val.length < policy!.minLength) {
    failed.push('minLength');
  }
  if (policy!.requireNumber && !NUMBER_REGEX.test(val)) {
    failed.push('requireNumber');
  }
  if (policy!.requireLowercase && !LOWERCASE_REGEX.test(val)) {
    failed.push('requireLowercase');
  }
  if (policy!.requireUppercase && !UPPERCASE_REGEX.test(val)) {
    failed.push('requireUppercase');
  }
  if (policy!.requireSpecial && !SPECIAL_REGEX.test(val)) {
    failed.push('requireSpecial');
  }
  return failed;
};

/** Convenience boolean check. */
export const validatePasswordAgainstPolicy = (
  value: string | null | undefined,
  policy?: PasswordPolicy | null
): boolean => getFailedPasswordRules(value, policy).length === 0;

/** Fixed error message for a single failed rule. */
export const getRuleMessage = (
  rule: PasswordPolicyRule,
  policy: PasswordPolicy | null | undefined,
  locale: string
): string => {
  const isJa = locale === 'ja';
  switch (rule) {
    case 'minLength':
      return isJa
        ? `${policy?.minLength ?? 0}文字以上で入力してください`
        : `Please enter at least ${policy?.minLength ?? 0} characters`;
    case 'requireNumber':
      return isJa
        ? '数字（0〜9）を1文字以上含めてください'
        : 'Please include at least one number (0-9)';
    case 'requireLowercase':
      return isJa
        ? '小文字（a〜z）を1文字以上含めてください'
        : 'Please include at least one lowercase letter (a-z)';
    case 'requireUppercase':
      return isJa
        ? '大文字（A〜Z）を1文字以上含めてください'
        : 'Please include at least one uppercase letter (A-Z)';
    case 'requireSpecial':
      return isJa
        ? '特殊文字（@$!%*#?&）を1文字以上含めてください'
        : 'Please include at least one special character (@$!%*#?&)';
  }
};

/**
 * Human-readable error built from the failed rules, one message per line.
 * Returns an empty string when nothing failed.
 */
export const buildPolicyMessage = (
  failedRules: PasswordPolicyRule[],
  policy: PasswordPolicy | null | undefined,
  locale: string
): string =>
  failedRules.map((rule) => getRuleMessage(rule, policy, locale)).join('\n');
